import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useState } from 'react';
import FilterChips from './FilterChips';
import SortBy from './SortBy';
import MultiSelectActions from './MultiSelectActions';
import { replaceParams } from '../../../services/content';
import styles from './SearchFilters.module.less';
import InteractiveWorksheetsFilter from './InteractiveWorksheetsFilter';
import { trackGA } from '../../../services/track';
import { FacetsMobileBtn } from '../../../edcom-ui';

function SearchFilters({
  total = 0,
  allFacets,
  filter,
  currentFilters,
  urlPath,
  onFilter,
  selected = [],
  open,
  setOpen,
  clearFilters,
}) {
  const [
    hasInteractiveWorksheetTooltip,
    setHasInteractiveWorksheetTooltip,
  ] = useState(false);

  const handleClickSort = sort => {
    const href = replaceParams(urlPath, { sort });
    onFilter(href);
  };

  const isInteractiveDisabled = () => {
    const interactiveFacet = allFacets.find(
      facet => facet.internalName === 'interactive'
    );
    return interactiveFacet ? interactiveFacet.disabled : true;
  };

  const handleCheckInteractive = async checked => {
    // remove cid
    const urlParts = window.location.href
      .replace(/\?cid=[\d.]+$/, '')
      .split('/');

    if (checked) {
      // We want to add "/interactive/" after the last facet part of the url, but before the query params
      const lastFacetUrlIndex =
        urlParts[urlParts.length - 1] === '?'
          ? urlParts.length - 2
          : urlParts.length - 1;
      urlParts.splice(lastFacetUrlIndex, 0, 'interactive');
    } else {
      urlParts.splice(urlParts.indexOf('interactive'), 1);
    }

    const newUrl = urlParts.join('/');
    const startIndex = newUrl.indexOf('/worksheets/');
    const href = newUrl.substring(startIndex);

    trackGA('Search', `interactive-only-filter-${checked}`);
    onFilter(href);
  };

  const handleShowInteractiveWorksheetTooltip = () => {
    setHasInteractiveWorksheetTooltip(!hasInteractiveWorksheetTooltip);
  };

  const handleClickFacetMobileBtn = async e => {
    e.preventDefault();
    setOpen(!open);
  };

  const interactive = !!filter.facets.interactive;

  const renderTotalResults = total => {
    if (total === 1) return '1 result';
    const formattedTotal = total
      .toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

    return `${formattedTotal} results`;
  };

  return (
    <>
      <div className={clsx(styles.overlay, open && styles.open)} />
      <div className={styles.container}>
        <FacetsMobileBtn onClick={handleClickFacetMobileBtn} />
        <div className={styles.numberWrapperMobile}>
          <span className={clsx(styles.numberText, styles.numberMobile)}>
            {renderTotalResults(total, currentFilters)}
          </span>
        </div>
        <div className={styles.leftFiltersWrapper}>
          <span className={styles.numberWrapper}>
            <span className={styles.numberText}>
              {renderTotalResults(total, currentFilters)}
            </span>
          </span>
          <span className={styles.chipWrapper}>
            <FilterChips
              filters={currentFilters}
              clickFilterChip={onFilter}
              handleClearAllClick={clearFilters}
            />
          </span>
        </div>
        <div className={styles.rightFiltersWrapper}>
          {selected.length > 0 && <MultiSelectActions selected={selected} />}
          {filter?.facets?.contentType === 'worksheets' &&
            !isInteractiveDisabled() && (
              <div className={styles.InteractiveFilterWrapper}>
                <InteractiveWorksheetsFilter
                  checked={interactive}
                  selectCallback={handleCheckInteractive}
                  hasTooltip={hasInteractiveWorksheetTooltip}
                  showTooltip={handleShowInteractiveWorksheetTooltip}
                />
              </div>
            )}
          <SortBy selectedSort={filter.sort} clickSort={handleClickSort} />
        </div>
      </div>
      <div className={styles.searchFilterContainer}></div>
    </>
  );
}

SearchFilters.displayName = 'SearchFilters';
SearchFilters.propTypes = {
  total: PropTypes.number,
  allFacets: PropTypes.array,
  filter: PropTypes.object,
  currentFilters: PropTypes.arrayOf(
    PropTypes.shape({
      display: PropTypes.string,
      group: PropTypes.string,
      href: PropTypes.string,
    })
  ),
  urlPath: PropTypes.string,
  /** Event handler for responding to filter changes **/
  onFilter: PropTypes.func,
  selected: PropTypes.array,
  showOnlySelected: PropTypes.bool,
  setShowOnlySelected: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  clearFilters: PropTypes.func,
};

export default SearchFilters;
